import { useEffect, useState } from 'react';
import {
  DomainObject,
  EvidenceProps,
  EvidenceTableProps,
  TableCells,
  TableColumn,
} from '@eas/common-web';
import { FilterTextCell } from '@composite/filter/filter-cells/filter-text-cell';

export function useEvidence<OBJECT extends DomainObject>({
  detailProps,
  ...props
}: EvidenceProps<OBJECT>): EvidenceProps<OBJECT> {
  const subtitle = detailProps?.toolbarProps?.subtitle;
  const title = detailProps?.toolbarProps?.title;
  const { width } = useWindowWidth();

  const deriveSubtitle = (item: OBJECT | null) =>
    typeof subtitle === 'string'
      ? subtitle
      : typeof subtitle === 'function'
      ? subtitle(item)
      : undefined;

  const deriveTitle = (item: OBJECT | null) =>
    typeof title === 'string'
      ? title
      : typeof title === 'function'
      ? title(item)
      : undefined;

  const documentTitle = (item: OBJECT | null): string => {
    const derivedSubtitle = deriveSubtitle(item);
    const derivedTitle = deriveTitle(item);

    return derivedSubtitle !== undefined && derivedSubtitle.length > 0
      ? `${derivedSubtitle} - ${detailProps?.toolbarProps?.title}`
      : derivedTitle ?? '';
  };

  return {
    ...props,
    tableProps: {
      ...props.tableProps,
      columns: defaultFilterComponents(props.tableProps),
    },
    detailProps: {
      ...detailProps,
      documentTitle,
    },
    switcherProps: {
      midPoint: 0.4 * width,
    },
  };
}

function getWindowWidth() {
  const { innerWidth: width } = window;

  return {
    width,
  };
}

export default function useWindowWidth() {
  const [windowWidth, setWindowWidth] = useState(getWindowWidth());

  useEffect(() => {
    function handleResize() {
      setWindowWidth(getWindowWidth());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowWidth;
}

const defaultFilterComponents = <T>(
  tableProps?: EvidenceTableProps<T>
): TableColumn<T, any>[] | undefined => {
  if (!tableProps?.columns) {
    return undefined;
  }
  const columns = tableProps.columns;
  const result = columns.map((c) => {
    if (c.CellComponent?.displayName !== TableCells.TextCell.displayName) {
      return c;
    }

    return {
      ...c,
      FilterComponent: c.FilterComponent ? c.FilterComponent : FilterTextCell,
    };
  });
  return result;
};
